import { HospitalityType, PaymentTerm, RentalTag, ViewType } from "../types";

export enum TrackCommentSource {
  track = "track",
  construction = "construction",
  trackFollowUp = "trackFollowUp",
}

export interface AddTrackWithCustomerDTO {
  customerName: string;
  customerPhone: string;
  compoundId: number;
  propertyTypeId: number;
  trackTypeId: number;
}

export interface AddTrackDTO {
  customerUId: string;
  customerTelephone: string;
  trackTypeId: number;
  leadTypeUId?: string;
  property: AddPropertyDTO;
  organizationUId: string;
}

export interface AddPropertyDTO {
  compoundId?: number;
  propertyTypeId?: number;
  unitId?: string;
  numberOfBedrooms?: number;
  numberOfBathrooms?: number;
  unitArea?: number;
  phase?: string;
  landArea?: number;
  gardenArea?: number;
  roofArea?: number;
  isUnderConstruction?: boolean;
  floorNumber?: number;
  numberOfFloors?: number;
  deliveryDate?: Date;
  readyToMove?: boolean;
  lastInstallmentDate?: Date;
  description?: string;
  finishing?: string;
  amenityIds?: number[];
  propertyPrice?: EditPropertyPriceDTO;
  latitude?: number;
  longitude?: number;
  address?: string;
  rentalTag?: RentalTag;
  hospitalityType?: HospitalityType;
}

export interface EditPropertyPriceDTO {
  paymentType?: PaymentTypes;
  priceRole: PriceRoles;

  price?: number;
  downPayment?: number;
  maintenance?: number;
  remainingInstallments?: number;
  clubhouse?: number;
  parking?: number;
  durationRemainingMonths?: number;
  durationRemainingYears?: number;
  installmentFrequency?: PaymentTerm;

  trackUid?: string;
}
export interface EditPropertyDTO {
  compoundId?: number;
  propertyTypeId?: number;
  unitId?: string;
  // null clears the column; undefined leaves it untouched (see optionalCount in ../utils).
  numberOfBedrooms?: number | null;
  numberOfBathrooms?: number | null;
  unitArea?: number | null;
  phase?: string;
  landArea?: number | null;
  gardenArea?: number | null;
  roofArea?: number | null;
  isUnderConstruction?: boolean;
  floorNumber?: number | null;
  numberOfFloors?: number | null;
  deliveryDate?: Date;
  lastInstallmentDate?: Date;
  readyToMove?: boolean;
  description?: string;
  finishing?: string;
  websiteListingId?: number | null;
  amenityIds?: number[];
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  rentalTag?: RentalTag;
  hospitalityType?: HospitalityType;
}

export interface EditFinishingInfoDTO {
  contractSellingPrice?: number;
  actualSellingPrice?: number;
  forcastedRentalUnits?: number;
  rentalTag?: RentalTag;
  hospitalityType?: HospitalityType;
}

export interface EditRentalDetailsDTO {
  availableFrom?: Date;
  rentalPrice?: number;
  showingFrom?: Date;
  annualIncreasePercent?: number | undefined;
  minimumStayMonths?: number | undefined;
  insuranceDepositRequired?: boolean | undefined;
  noBrokerCommission?: boolean | undefined;
  furnished?: boolean | undefined;
  paymentTerms?: PaymentTerm;
}

export enum PaymentTypes {
  CASH = "cash",
  INSTALLMENTS = "installments",
}

export enum PriceRoles {
  LISTING = "listing",
  ASKING = "asking",
}
export interface AddSubPropertyDTO {
  trackTypeId: number;
  property: AddPropertyDTO;
}

export interface AddTrackToPropertyDTO {
  trackTypeId: number;
}
export enum ListingType {
  RENTAL = "rental",
  RESALE = "resale",
}

interface ListingBaseDTO {
  documentUids?: string[];
  listingType: ListingType;
  propertyRental?: EditRentalDetailsDTO;
  isRented?: boolean;
  propertyPrice?: EditPropertyPriceDTO;
  publish?: boolean;
  property?: EditPropertyDTO;
  descriptionAr?: string;
  descriptionEn?: string;
}

export enum CurrencyCode {
  EGP = "EGP",
  USD = "USD",
  EUR = "EUR",
}

export enum RoomType {
  EntireHome = "EntireHome",
  PrivateRoom = "PrivateRoom",
  SharedRoom = "SharedRoom",
}

export const RoomTypeLabels: Record<RoomType, string> = {
  [RoomType.EntireHome]: "Entire Home",
  [RoomType.PrivateRoom]: "Private Room",
  [RoomType.SharedRoom]: "Shared Room",
};

// Inclusive `yyyy-MM-dd`.
export interface ExceptionalPriceDTO {
  fromDate: string;
  toDate: string;
  price: number;
}

export interface AddHospitalityListingDTO {
  name: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  price: number;
  priceForExtraPerson?: number;
  currencyCode: CurrencyCode;
  specialInstruction?: string;
  checkInTimeStart?: number;
  checkInTimeEnd?: number;
  checkOutTime?: number;
  roomTypeId?: number;
  minNights?: number;
  maxNights?: number;
  airbnbOfficialListingMarkup?: number;
  bookingEngineMarkup?: number;
  isActive: boolean;
  isFeatured?: boolean;
  propertyUid: string;
  propertyTrackId?: number;
  documentUids?: string[];
  maxNumberOfGuests?: number;
  bedCount?: number;
  singleBedCount?: number;
  kingBedCount?: number;
  twinBedCount?: number;
  queenBedCount?: number;
  sofaBedCount?: number;
  viewType?: ViewType;
  isExtraBedAllowed?: boolean;
  isSmokingAllowed?: boolean;
  isPetsAllowed?: boolean;
  isPartiesAllowed?: boolean;
  instantBookable: boolean;
  exceptionalPrices?: ExceptionalPriceDTO[];
}

export interface EditHospitalityListingDTO {
  name?: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  price?: number;
  priceForExtraPerson?: number;
  currencyCode?: CurrencyCode;
  specialInstruction?: string;
  checkInTimeStart?: number;
  checkInTimeEnd?: number;
  checkOutTime?: number;
  roomTypeId?: number;
  minNights?: number;
  maxNights?: number;
  airbnbOfficialListingMarkup?: number;
  bookingEngineMarkup?: number;
  isActive?: boolean;
  isFeatured?: boolean;
  propertyTrackId?: number;
  documentUids?: string[];
  maxNumberOfGuests?: number;
  bedCount?: number;
  singleBedCount?: number;
  kingBedCount?: number;
  twinBedCount?: number;
  queenBedCount?: number;
  sofaBedCount?: number;
  viewType?: ViewType;
  isExtraBedAllowed?: boolean;
  isSmokingAllowed?: boolean;
  isPetsAllowed?: boolean;
  isPartiesAllowed?: boolean;
  instantBookable?: boolean;
  exceptionalPrices?: ExceptionalPriceDTO[];
}

export interface AddListingDTO extends ListingBaseDTO {
  trackUid: string;
}

export interface EditListingDTO extends Partial<ListingBaseDTO> {}

export interface ActiveListingDto {
  propertyUid: string;
  listingType: ListingType;
}
